'use client';

import { useState, useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Icon from '@/components/ui/AppIcon';

interface RuleItem {
  text: string;
  severity: 'warning' | 'kick' | 'ban';
}

interface RuleCategory {
  id: string;
  title: string;
  summary: string;
  icon: string;
  iconColor: string;
  rules: RuleItem[];
}

// Shared rule categories (same for all servers)
const SHARED_RULE_CATEGORIES: RuleCategory[] = [
  {
    id: 'chat',
    title: 'Chat Rules',
    summary: 'Keep communication respectful and appropriate',
    icon: 'ChatBubbleLeftIcon',
    iconColor: '#6a5cff',
    rules: [
      { text: 'No spam - posting the same message more than twice within 60 seconds', severity: 'warning' },
      { text: 'No external links or server advertisements', severity: 'kick' },
      { text: 'No racial slurs, homophobic slurs, or targeting players based on identity', severity: 'ban' },
      { text: 'No stating the server is bad or telling players to leave', severity: 'kick' },
      { text: 'Use F7 report system or Discord tickets only - no chat reports', severity: 'warning' },
      { text: 'No doxxing - sharing real-life information about players (except Steam profiles for reports)', severity: 'ban' },
      { text: 'No impersonating server staff members', severity: 'ban' },
      { text: 'No racist, sexual, or offensive imagery in signs or paintings', severity: 'kick' }
    ]
  },
  {
    id: 'cheating',
    title: 'Cheating',
    summary: 'Zero tolerance for unfair advantages',
    icon: 'NoSymbolIcon',
    iconColor: '#ef4444',
    rules: [
      { text: 'Using third-party software for advantages', severity: 'ban' },
      { text: 'Ban evasion attempts', severity: 'ban' },
      { text: 'Playing with active EAC Rust Game Ban within last 180 days', severity: 'ban' },
      { text: 'Using macros, scripts, or programmable features to automate actions', severity: 'ban' },
      { text: 'Exploiting lag, desync, or connection issues for advantages', severity: 'ban' }
    ]
  },
  {
    id: 'exploits',
    title: 'Exploits & Bugs',
    summary: 'Report bugs, don\'t abuse them',
    icon: 'BugAntIcon',
    iconColor: '#22c55e',
    rules: [
      { text: 'Clipping through terrain, rocks, or map objects', severity: 'ban' },
      { text: 'Building loot boxes inside terrain or map objects', severity: 'ban' },
      { text: 'Entering or shooting from inside rocks or unintended spaces', severity: 'ban' },
      { text: 'Placing deployables where they cannot be destroyed', severity: 'kick' },
      { text: 'Using plugin errors or server bugs for advantages', severity: 'ban' },
      { text: 'Building items that cause server performance issues', severity: 'kick' }
    ]
  }
];

// Server-specific TEAMING rules
const TEAMING_RULES: Record<string, RuleCategory> = {
  main: {
    id: 'teaming',
    title: 'Teaming (Max 8)',
    summary: 'Teams up to 8 players allowed',
    icon: 'UserGroupIcon',
    iconColor: '#f59e0b',
    rules: [
      { text: 'Exceeding the 8 player team limit', severity: 'ban' },
      { text: 'Allying with other teams to exceed team limit', severity: 'ban' },
      { text: 'Using alternate accounts to bypass team limits', severity: 'ban' },
      { text: 'Coordinating with other teams during raids or PvP', severity: 'kick' }
    ]
  },
  solo: {
    id: 'teaming',
    title: 'Teaming (Solo Only)',
    summary: 'Strictly solo - no cooperation allowed',
    icon: 'UserGroupIcon',
    iconColor: '#f59e0b',
    rules: [
      { text: 'Having multiple players with access to same base or resources', severity: 'ban' },
      { text: 'Performing any activities with another player', severity: 'ban' },
      { text: 'Leaving loot for other players to collect later', severity: 'kick' },
      { text: 'Entering another player\'s base in a friendly manner', severity: 'kick' }
    ]
  },
  trio: {
    id: 'teaming',
    title: 'Teaming (Max 3)',
    summary: 'Teams up to 3 players maximum',
    icon: 'UserGroupIcon',
    iconColor: '#f59e0b',
    rules: [
      { text: 'Having more than 3 players with access to same base or resources', severity: 'ban' },
      { text: 'Having more than 3 players authorized on same tool cupboard', severity: 'ban' },
      { text: 'Performing activities with players outside your team', severity: 'kick' },
      { text: 'Leaving loot for other teams to collect later', severity: 'kick' },
      { text: 'Entering another team\'s base in a friendly manner', severity: 'kick' }
    ]
  }
};

const SERVERS = [
  { id: 'main', name: 'Main EU', teamLimit: 8 },
  { id: 'solo', name: 'Solo EU', teamLimit: 1 },
  { id: 'trio', name: 'Trio EU', teamLimit: 3 }
];

export default function RulesPage() {
  const [selectedServer, setSelectedServer] = useState('main');
  const [selectedCategory, setSelectedCategory] = useState<RuleCategory | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const sectionRef = useRef<HTMLDivElement>(null);

  // Get server-specific rules
  const getRulesForServer = (serverId: string): RuleCategory[] => {
    const teamingRule = TEAMING_RULES[serverId];
    return [
      SHARED_RULE_CATEGORIES[0], // CHAT
      SHARED_RULE_CATEGORIES[1], // CHEATING
      teamingRule,                // TEAMING (server-specific)
      SHARED_RULE_CATEGORIES[2]  // EXPLOITS
    ];
  };

  const currentRules = getRulesForServer(selectedServer);

  // Filter rules based on search
  const filteredRules = currentRules.map(category => ({
    ...category,
    rules: category.rules.filter(rule => 
      rule.text.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.rules.length > 0);

  const displayRules = searchQuery ? filteredRules : currentRules;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.08 }
    );

    const els = sectionRef?.current?.querySelectorAll('.reveal-hidden');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'warning': return 'text-emerald-400';
      case 'kick': return 'text-yellow-400';
      case 'ban': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getSeverityBg = (severity: string) => {
    switch (severity) {
      case 'warning': return 'bg-emerald-500/10 border-emerald-500/30';
      case 'kick': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'ban': return 'bg-red-500/10 border-red-500/30';
      default: return 'bg-gray-500/10 border-gray-500/30';
    }
  };

  return (
    <>
      <div className="vignette" aria-hidden="true" />
      <Header />
      
      <main className="relative pt-24 pb-20 px-6" style={{ zIndex: 2 }}>
        <div className="max-w-7xl mx-auto" ref={sectionRef}>
          {/* Top Section */}
          <div className="text-center mb-12 reveal-hidden">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-extrabold mb-4 leading-tight">
              <span className="text-white">Server </span>
              <span className="gradient-text">Rules</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 font-medium max-w-2xl mx-auto">
              Fair play and competitive integrity are the foundation of our community
            </p>
          </div>

          {/* Server Selector Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12 reveal-hidden">
            {SERVERS.map((server) => (
              <button
                key={server.id}
                onClick={() => setSelectedServer(server.id)}
                className={`px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 ${
                  selectedServer === server.id
                    ? 'bg-gradient-to-r from-vanta-accent to-purple-600 text-white shadow-lg shadow-vanta-accent/50 scale-105'
                    : 'glass text-vanta-muted hover:text-white hover:border-vanta-accent/30 hover:scale-105'
                }`}
              >
                {server.name}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12 reveal-hidden">
            <div className="relative">
              <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-vanta-muted" />
              <input
                type="text"
                placeholder="Search Rules..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 glass rounded-xl text-white placeholder-vanta-muted focus:outline-none focus:ring-2 focus:ring-vanta-accent/50 transition-all"
              />
            </div>
          </div>

          {/* Rule Severity Legend */}
          <div className="glass rounded-2xl p-6 mb-12 reveal-hidden">
            <h3 className="text-lg font-heading font-700 text-white mb-4 text-center">Rule Severity Legend</h3>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-emerald-400 shadow-lg shadow-emerald-400/50" />
                <span className="text-sm font-medium text-emerald-400">Warning</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50" />
                <span className="text-sm font-medium text-yellow-400">Kick</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full bg-red-400 shadow-lg shadow-red-400/50" />
                <span className="text-sm font-medium text-red-400">Permanent Ban</span>
              </div>
            </div>
          </div>

          {/* Rule Category Cards Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {displayRules.map((category, index) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category)}
                className="glass rounded-2xl p-8 text-left transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-vanta-accent/30 hover:border-vanta-accent/50 group reveal-hidden"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
                  style={{ 
                    backgroundColor: `${category.iconColor}20`,
                    boxShadow: `0 0 20px ${category.iconColor}40`
                  }}
                >
                  <Icon
                    name={category.icon as any}
                    size={32}
                    style={{ color: category.iconColor }}
                  />
                </div>
                <h3 className="text-2xl font-heading font-800 text-white mb-3 group-hover:text-vanta-accent transition-colors">
                  {category.title}
                </h3>
                <p className="text-sm text-vanta-muted mb-4 leading-relaxed">
                  {category.summary}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-vanta-accent">
                    {category.rules.length} {category.rules.length === 1 ? 'rule' : 'rules'}
                  </span>
                  <Icon name="ArrowRightIcon" size={20} className="text-vanta-muted group-hover:text-vanta-accent group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            ))}
          </div>

          {/* Bottom Action Section */}
          <div className="grid md:grid-cols-2 gap-6 reveal-hidden">
            {/* Panel 1: Report a Player */}
            <div className="glass rounded-2xl p-8 hover:shadow-2xl hover:shadow-red-500/20 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <Icon name="ExclamationTriangleIcon" size={28} className="text-red-400" />
                </div>
                <h3 className="text-2xl font-heading font-800 text-white">Report a Player</h3>
              </div>
              <p className="text-vanta-muted mb-6 leading-relaxed">
                Found someone breaking the rules? Submit a detailed report with evidence via our Discord support system.
              </p>
              <a
                href="https://discord.gg/gJgHMDZAES"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary w-full py-4 text-base flex items-center justify-center gap-3 font-semibold"
              >
                <Icon name="FlagIcon" size={20} />
                Report via Discord
              </a>
            </div>

            {/* Panel 2: Join Community */}
            <div className="glass rounded-2xl p-8 hover:shadow-2xl hover:shadow-[#5865F2]/20 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl bg-[#5865F2]/20 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                  </svg>
                </div>
                <h3 className="text-2xl font-heading font-800 text-white">Join Community</h3>
              </div>
              <p className="text-vanta-muted mb-6 leading-relaxed">
                Connect with thousands of players, get updates, participate in events, and be part of our growing community.
              </p>
              <a
                href="https://discord.gg/gJgHMDZAES"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary w-full py-4 text-base flex items-center justify-center gap-3 font-semibold"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                </svg>
                Discord
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Modal Popup */}
      {selectedCategory && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6 z-50 transition-all duration-300 ease-out"
          style={{
            animation: 'fadeIn 0.3s ease-out'
          }}
          onClick={() => setSelectedCategory(null)}
        >
          <div 
            className="glass rounded-3xl max-w-3xl w-full max-h-[80vh] overflow-y-auto p-8 transition-all duration-500 ease-out"
            style={{
              animation: 'slideUp 0.4s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300"
                  style={{ 
                    backgroundColor: `${selectedCategory.iconColor}20`,
                    boxShadow: `0 0 20px ${selectedCategory.iconColor}40`
                  }}
                >
                  <Icon
                    name={selectedCategory.icon as any}
                    size={28}
                    style={{ color: selectedCategory.iconColor }}
                  />
                </div>
                <div>
                  <h2 className="text-3xl font-heading font-800 text-white mb-1">
                    {selectedCategory.title}
                  </h2>
                  <p className="text-sm text-vanta-muted">{selectedCategory.summary}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCategory(null)}
                className="w-10 h-10 rounded-lg glass hover:bg-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110 hover:rotate-90 active:scale-95"
              >
                <Icon name="XMarkIcon" size={24} className="text-vanta-muted" />
              </button>
            </div>

            {/* Rules List */}
            <div className="space-y-3">
              {selectedCategory.rules.map((rule, idx) => (
                <div
                  key={idx}
                  className={`rounded-xl border-2 p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg ${getSeverityBg(rule.severity)}`}
                  style={{
                    animation: `slideUp 0.3s ease-out ${idx * 0.05}s backwards`
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center font-bold text-white">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-200 leading-relaxed mb-2">{rule.text}</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold uppercase tracking-wider ${getSeverityColor(rule.severity)}`}>
                          {rule.severity === 'warning' ? '⚠️ Warning' : rule.severity === 'kick' ? '⚡ Kick' : '🚫 Permanent Ban'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}